import Alert from 'react-bootstrap/Alert';
import { FormikErrors } from 'formik';
import { useEffect, useMemo } from 'react';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { useFrontAndBackAttributeImages } from '@hooks/use-front-and-back-attribute-images';
import { ImageAttribute } from '../common/image-attribute/image-attribute';
import { AttributeTypes } from '@iddataweb/idw-common-ui/enums';
import { useSelector } from 'react-redux';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import styles from "./front-and-back-attribute-images.less";

type FrontAndBackAttributeImagesProps = {
    onSetSubmitValue: (payload: any) => void;
    onSetPickerValue: (payload: any) => void;
    attribute: AttributeFormState;
    fieldName: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;

    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
};

export const FrontAndBackAttributeImages = (props: FrontAndBackAttributeImagesProps) => {
    const {
        attribute,
        fieldName,
        setFieldValue,
        setFieldTouched,
        onSetSubmitValue,
        onSetPickerValue
    } = props;

    const content = useSelector(getLanguageContent);
    const images = useFrontAndBackAttributeImages(attribute, onSetSubmitValue, onSetPickerValue);
    const [frontImage, backImage] = images;

    const setFieldValues = async (frontImage: string, backImage: string) => {
        if (frontImage) {
            await setFieldValue(`${fieldName}.frontImage`, frontImage);
            setFieldTouched(`${fieldName}.frontImage`, true, true);
        } else {
            await setFieldValue(`${fieldName}.frontImage`, '');
            setFieldTouched(`${fieldName}.frontImage`, true, true);
        }
        if (backImage) {
            await setFieldValue(`${fieldName}.backImage`, backImage);
            setFieldTouched(`${fieldName}.backImage`, true, true);
        } else {
            await setFieldValue(`${fieldName}.backImage`, '');
            setFieldTouched(`${fieldName}.backImage`, true, true);
        }
    };

    useEffect(() => {
        setFieldValues(frontImage, backImage);
    }, [frontImage, backImage]);

    const missingFrontImageText = useMemo(() => {
        switch (attribute.type) {
            case AttributeTypes.DRIVERS_LICENSE_IMAGES:
                return content.form.driverLicenseImages.missingFrontImage;
            case AttributeTypes.AADHAR_CARD_IMAGES:
                return content.form.aadharCardImages.missingFrontImage;
            case AttributeTypes.PAN_IMAGES:
                return content.form.panCardImages.missingFrontImage;
            case AttributeTypes.ID_CARD_IMAGES:
                return content.form.idCardImages.missingFrontImage;
            default:
                return "";
        }
    }, [content, attribute.type]);

    const missingBackImageText = useMemo(() => {
        switch (attribute.type) {
            case AttributeTypes.DRIVERS_LICENSE_IMAGES:
                return content.form.driverLicenseImages.missingBackImage;
            case AttributeTypes.AADHAR_CARD_IMAGES:
                return content.form.aadharCardImages.missingBackImage;
            case AttributeTypes.PAN_IMAGES:
                return content.form.panCardImages.missingBackImage;
            case AttributeTypes.ID_CARD_IMAGES:
                return content.form.idCardImages.missingBackImage;
            default:
                return "";
        }
    }, [content, attribute.type]);

    const imageLabels = useMemo(() => {
        switch (attribute.type) {
            case AttributeTypes.DRIVERS_LICENSE_IMAGES:
                return ["Preview Front Image of Drivers License", "Preview Back Image of Drivers License"];
            case AttributeTypes.AADHAR_CARD_IMAGES:
                return ["Preview Front Image of Aadhar Card", "Preview Back Image of Aadhar Card"];
            case AttributeTypes.PAN_IMAGES:
                return ["Preview Front Image of PAN", "Preview Back Image of PAN"];
            case AttributeTypes.ID_CARD_IMAGES:
                return ["Preview Front Image of ID Card", "Preview Back Image of ID Card"];
            default:
                return ["", ""];
        }
    }, [content, attribute.type]);

    return (
        <div className={styles.frontAndBackAttributeImages}>
            {!frontImage && (
                <Alert variant={"danger"}>
                    {missingFrontImageText}
                </Alert>
            )}
            {!backImage && (
                <Alert variant={"danger"}>
                    {missingBackImageText}
                </Alert>
            )}
            {frontImage && backImage && (
                <ImageAttribute
                    id={`image-attribute-${attribute.id}`}
                    images={images}
                    showGalleryInitially={false}
                    centerPreviewMessage={true}
                    imageLabels={imageLabels}
                />
            )}
        </div>
    );
};